/**
 * useDoneActions — TanStack Query hooks for the Done Actions page (TECH-27).
 *
 * One read query and three mutations. Each mutation invalidates the keys
 * documented in TECH-86's invalidation map — done list always, financial-
 * impact only when the aggregate could move, and the active list when the
 * action's done state flips back to undone.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import client from '../api/client.js'

const QK_DONE = ['actions', 'done']
const QK_ACTIONS = ['actions']
// Must match the financial-impact query key in useFinancials.js (['actions-financial', scope]).
// A prefix of ['actions-financial'] invalidates every scope.
const QK_FINANCIAL_IMPACT = ['actions-financial']

/** Read — full list of marked-done actions with latest snapshot + actual value. */
export function useDoneActions() {
  return useQuery({
    queryKey: QK_DONE,
    queryFn: async () => {
      const { data } = await client.get('/actions/done')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

/** Audit log helper — used by ActionHistoryTimeline to fetch one action's event stream. */
export function useActionAuditEvents(actionId) {
  return useQuery({
    // Per-action history endpoint — available to all authenticated users
    // (customer responses are sanitised server-side), unlike the cerebi-only
    // /api/audit/* used by the global Audit page.
    queryKey: ['action-history', actionId],
    enabled: Boolean(actionId),
    queryFn: async () => {
      const { data } = await client.get(`/actions/${actionId}/history`)
      return data
    },
    staleTime: 30 * 1000,
  })
}

/** Toggle an action back to active. Invalidates everywhere — it just left the Done bucket. */
export function useMarkUndone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (actionId) => {
      const { data } = await client.post(`/actions/${actionId}/undone`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_DONE })
      qc.invalidateQueries({ queryKey: QK_ACTIONS })
      qc.invalidateQueries({ queryKey: QK_FINANCIAL_IMPACT })
    },
  })
}

/** Mark an action accepted/done (used by ActionCenter's Accept button). */
export function useMarkDone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, note }) => {
      const { data } = await client.post(
        `/actions/${actionId}/done`,
        note ? { note } : undefined,
      )
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_DONE })
      qc.invalidateQueries({ queryKey: QK_ACTIONS })
      qc.invalidateQueries({ queryKey: QK_FINANCIAL_IMPACT })
    },
  })
}

/** Mark an action dismissed — terminal outcome, same workflow as done but
 *  recorded as a distinct event_type so the Done page can render the
 *  Accepted vs Dismissed distinction.
 */
export function useMarkDismissed() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, note }) => {
      const { data } = await client.post(
        `/actions/${actionId}/dismiss`,
        note ? { note } : undefined,
      )
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_DONE })
      qc.invalidateQueries({ queryKey: QK_ACTIONS })
      qc.invalidateQueries({ queryKey: QK_FINANCIAL_IMPACT })
    },
  })
}

/** Record the manager's actual realized P&L. Negative values allowed. */
export function useSetActualValue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, valueUsd }) => {
      const { data } = await client.put(
        `/actions/${actionId}/actual-value`,
        { value_usd: valueUsd },
      )
      return data
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: QK_DONE })
      qc.invalidateQueries({ queryKey: QK_FINANCIAL_IMPACT })
      // Refresh the single-action audit thread for the open dialog.
      qc.invalidateQueries({ queryKey: ['action-history', variables.actionId] })
    },
  })
}

/** Append a comment to an action's audit thread. Additive — never invalidates aggregates. */
export function useAddComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, text }) => {
      const { data } = await client.post(
        `/actions/${actionId}/comments`,
        { text },
      )
      return data
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['action-history', variables.actionId] })
    },
  })
}
